import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Appstore } from "../../redux/store";
import { NewEvent } from "../../redux/states/event.state";
import { getEventById } from "../../Services/Event";
import { toastError } from "../../utils/toast";
import { IEvent } from "../../interfaces/models/IEvent";

const inflightEventLoads = new Map<string, Promise<IEvent | null>>();

function fetchEventById(eventId: string): Promise<IEvent | null> {
  const existing = inflightEventLoads.get(eventId);
  if (existing) {
    return existing;
  }

  const promise = getEventById(eventId).finally(() => {
    inflightEventLoads.delete(eventId);
  });
  inflightEventLoads.set(eventId, promise);
  return promise;
}

/** True when Redux has the event id but lacks fields only set by getEventById(withWSG). */
function isEventMissingEnrichedFields(event: IEvent): boolean {
  const hasContactUsIds =
    Array.isArray(event.contactUs) && event.contactUs.length > 0;
  if (hasContactUsIds && event.contactUsDetails === undefined) {
    return true;
  }

  if (event.specialEventTemplateId && event.specialEventTemplate === undefined) {
    return true;
  }

  return false;
}

function eventNeedsLoad(event: IEvent, eventId: string): boolean {
  if (!event.id || event.id === "" || event.id !== eventId) {
    return true;
  }

  return isEventMissingEnrichedFields(event);
}

export function useEventLoader(
  eventId: string | undefined,
  shouldLoad: boolean = true
): IEvent | null {
  const dispatch = useDispatch();
  const reduxEvent = useSelector((state: Appstore) => state.event);

  const needsLoad = useMemo(
    () => (eventId ? eventNeedsLoad(reduxEvent, eventId) : false),
    [eventId, reduxEvent]
  );

  useEffect(() => {
    if (!shouldLoad || !eventId || !needsLoad) {
      return;
    }

    let cancelled = false;

    const loadEvent = async () => {
      try {
        const eventData = await fetchEventById(eventId);
        if (!cancelled && eventData) {
          dispatch(NewEvent(eventData));
        }
      } catch (error) {
        if (!cancelled) {
          toastError("Error al cargar el evento");
        }
      }
    };

    loadEvent();

    return () => {
      cancelled = true;
    };
  }, [eventId, dispatch, shouldLoad, needsLoad]);

  return useMemo(() => {
    if (reduxEvent?.id && reduxEvent.id === eventId) {
      return reduxEvent;
    }
    return null;
  }, [reduxEvent, eventId]);
}
